import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Real projects data from brief
const projects = [
  {
    id: "anima",
    title: "Restaurant Anima",
    location: "Paris",
    category: "Restauration",
    description: "Installation d'un système silencieux avec grille acoustique pour l'unité extérieure. Solution sur mesure permettant de respecter les contraintes sonores strictes du voisinage parisien tout en assurant un confort thermique optimal pour les clients du restaurant.",
    specs: ["Grille acoustique sur mesure", "Unité extérieure silencieuse", "Régulation zone par zone"],
    image: "/images/projects/restaurant-anima.webp"
  },
  {
    id: "calogero",
    title: "Studio Calogero",
    location: "Boulogne-Billancourt",
    category: "Studio musique & Résidentiel",
    description: "Climatisation et ventilation intégrées dans une isolation acoustique type boîte dans la boîte. Projet combinant espace de création musicale professionnelle et habitat, nécessitant une approche acoustique de très haut niveau pour garantir un silence absolu en enregistrement.",
    specs: ["Isolation acoustique boîte dans la boîte", "Ventilation silencieuse NC15", "Double usage studio/résidentiel"],
    image: "/images/projects/studio-calogero.webp"
  },
  {
    id: "after-now",
    title: "Studio After Now",
    location: "Toulouse",
    category: "Studio de production",
    description: "Installation complète CVC avec 3 gainables équipés du système Airzone et mini VRV, incluant une CTA double flux pour le renouvellement d'air. Projet d'envergure intégrant les dernières technologies de régulation zone par zone pour un confort optimal dans chaque espace du studio.",
    specs: ["3 gainables avec système Airzone", "Mini VRV haute performance", "CTA double flux", "Installation complète CVC"],
    image: "/images/projects/studio-after-now.webp"
  }
];

// Brands installed
const brands = ["Daikin", "Mitsubishi", "Hitachi", "Bosch"];

export default function RealisationsPage() {
  return (
    <>
      <Helmet>
        <title>Nos réalisations HVAC | FLOW - Climatisation Paris</title>
        <meta name="description" content="Découvrez nos réalisations en climatisation et ventilation : Restaurant Anima (Paris), Studio Calogero (Boulogne-Billancourt), Studio After Now (Toulouse). Expertise HVAC haut de gamme." />
        <link rel="canonical" href="https://flow-air.com/realisations" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Portfolio</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nos <span className="text-[#00AFFF]">réalisations</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              Découvrez nos projets d'installation HVAC pour des clients exigeants : 
              restaurants, studios d'enregistrement et espaces professionnels.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24">
        <div className="container-flow">
          <div className="space-y-16 md:space-y-24">
            {projects.map((project, index) => (
              <motion.article 
                key={project.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src={project.image} 
                      alt={`${project.title} - ${project.location} - Installation HVAC FLOW`}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 bg-[#00AFFF] text-white text-xs font-medium rounded-full">
                        {project.category}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center gap-2 text-[#475569] text-sm mb-3">
                    <MapPin size={16} className="text-[#00AFFF]" />
                    <span>{project.location}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0E1A2B] mb-4">{project.title}</h2>
                  <p className="text-[#475569] leading-relaxed mb-6">{project.description}</p>
                  
                  {/* Specs */}
                  <div className="space-y-2 mb-6">
                    {project.specs.map((spec, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00AFFF]" />
                        <span className="text-sm text-[#0E1A2B]">{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-12 md:py-16 bg-[#F5F7FA]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center">
            <span className="caption mb-4 block">Marques installées</span>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {brands.map((brand, index) => (
                <span 
                  key={brand} 
                  className="text-lg font-bold tracking-wider text-[#94A3B8] hover:text-[#0E1A2B] transition-colors"
                >
                  {brand.toUpperCase()}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Vous avez un projet similaire ?
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez-nous pour discuter de vos besoins en climatisation et ventilation. 
              Devis gratuit sous 24 heures.
            </p>
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center gap-2"
            >
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
